import { CampaignType, FilterCategory } from 'models/enums';
import { actionTypes } from './customCampaignActions';
import {
    ABTestConfig,
    CampaignChannels,
    DirectMailSendInputState,
    EmailSendInputState,
    ReEntryFields,
    SendRecurrenceFrequency,
    SmsSendInputState,
    SplitActions,
    SplitTypes,
    VoiceCallSendInput,
} from 'domains/campaigns/types';

import {
    EngagementAPIOperator,
    EngagementUIOperator,
    SegmentFilterCategorySpecificField,
    SegmentFilterState,
} from 'domains/segments/types';

import { engagementUiToApiOperator } from 'domains/segments/utils';
import { calculateComparisonFields } from 'domains/campaigns/utils';

export type CampaignSend = DirectMailSendInputState | EmailSendInputState | SmsSendInputState | VoiceCallSendInput;

export type CampaignStateTypes = {
    name: string;
    type: CampaignType;
    sends: CampaignSend[];
    reentryDetails?: ReEntryFields;
    abTestConfig?: ABTestConfig;
};

export const directMailInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            channel: CampaignChannels.DIRECT_MAIL,
            cohortId: '',
            content: {
                id: null,
                name: '',
            },
            name: '',
            segmentId: null,
            scheduledTimestamp: null,
            senderProfileId: null,
        },
    ],
    reentryDetails: null,
};

export const emailInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            channel: CampaignChannels.EMAIL,
            cohortId: '',
            name: '',
            preHeader: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledEndTimestamp: null,
            scheduledTimestamp: null,
            segmentId: null,
            senderProfileId: null,
            subjectLine: '',
            templateId: null,
        },
    ],
    reentryDetails: null,
    abTestConfig: null,
};

export const smsInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            channel: CampaignChannels.SMS,
            cohortId: '',
            content: {
                id: null,
                name: '',
            },
            name: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledEndTimestamp: null,
            scheduledTimestamp: null,
            segmentId: null,
            smsSenderProfileId: null,
        },
    ],
    reentryDetails: null,
};

export const voiceCallInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            callerProfileId: null,
            channel: CampaignChannels.VOICE_CALL,
            cohortId: '',
            content: {
                id: null,
                name: '',
            },
            name: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledTimestamp: null,
            segmentId: null,
        },
    ],
    reentryDetails: null,
};

const additionalEmailSend: EmailSendInputState = {
    channel: CampaignChannels.EMAIL,
    name: '',
    parentIndex: null,
    preHeader: '',
    senderProfileId: null,
    subjectLine: '',
    templateId: null,
};

function getUpdatedSends(sends: CampaignStateTypes['sends'], activeSendIndex: number, updater: any) {
    return sends.map((send, index) => {
        if (index === activeSendIndex) return { ...send, ...updater };
        return send;
    });
}

function getUpdatedSendsWithSegments(
    sends: CampaignStateTypes['sends'],
    meetsCriteriaSegment: SegmentFilterState<SegmentFilterCategorySpecificField>,
    activeSendIndex: number
) {
    const { category } = meetsCriteriaSegment;

    if (category === FilterCategory.CONTACT_ACTIVITY) {
        const sendsWithMeetsCriteriaSegments = getUpdatedSends(sends, activeSendIndex, {
            segments: [meetsCriteriaSegment],
        });

        const doesNotMeetCriteriaOperator = Object.values(EngagementAPIOperator).filter(
            (operator) =>
                operator !==
                engagementUiToApiOperator[(meetsCriteriaSegment.operator as unknown) as EngagementUIOperator]
        )[0];
        const doesNotMeetCriteriaSegment = {
            ...meetsCriteriaSegment,
            operator: doesNotMeetCriteriaOperator,
        };

        const sendWithSegments = getUpdatedSends(sendsWithMeetsCriteriaSegments, activeSendIndex + 1, {
            segments: [doesNotMeetCriteriaSegment],
        });

        return sendWithSegments;
    }

    if (category === FilterCategory.CONTACT_DETAILS || category === FilterCategory.CONTACT_EVENTS) {
        const sendsWithMeetsCriteriaSegments = getUpdatedSends(sends, activeSendIndex, {
            segments: [meetsCriteriaSegment],
        });

        // While the campaign is being built out, the segments for meets and
        // does not meet criteria will be the same. We will tell the backend
        // to negate the query associated with the does not meet criteria segment
        // upon campaign publish.
        const doesNotMeetCriteriaSegment = meetsCriteriaSegment;

        const sendWithSegments = getUpdatedSends(sendsWithMeetsCriteriaSegments, activeSendIndex + 1, {
            segments: [doesNotMeetCriteriaSegment],
        });

        return sendWithSegments;
    }
}

/**
 * Helper function to update comparison fields in AB test config
 * Only updates if AB testing is enabled and there are two email sends
 */
function updateABTestComparisonFields(state: CampaignStateTypes): CampaignStateTypes {
    // Only proceed if AB testing is enabled
    if (!state.abTestConfig || !state.abTestConfig.testOn) {
        return state;
    }

    // Only calculate for content AB testing with email sends
    if (state.abTestConfig.testOn !== 'content') {
        return state;
    }

    // Make sure we have exactly 2 email sends
    if (
        state.sends.length !== 2 ||
        state.sends[0].channel !== CampaignChannels.EMAIL ||
        state.sends[1].channel !== CampaignChannels.EMAIL
    ) {
        return state;
    }

    // Calculate comparison fields
    const comparisonFields = calculateComparisonFields(
        state.sends[0] as EmailSendInputState,
        state.sends[1] as EmailSendInputState
    );

    // Update abTestConfig with comparisonFields
    return {
        ...state,
        abTestConfig: {
            ...state.abTestConfig,
            comparisonFields,
        },
    };
}

export function customCurationReducer(state: CampaignStateTypes, action: any) {
    switch (action.type) {
        case actionTypes.SET_NAME: {
            return {
                ...state,
                name: action.name,
            };
        }
        case actionTypes.SELECT_AUDIENCE: {
            return {
                ...state,
                sends: [
                    {
                        ...state.sends[0],
                        segmentId: action.selectedAudienceId,
                    },
                    ...state.sends.slice(1),
                ],
            };
        }
        case actionTypes.SET_COHORT_ID: {
            const { cohortId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { cohortId });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_DIRECT_MAIL_CONTENT: {
            const { contentId, contentName, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: { id: contentId, name: contentName },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_VOICE_CALL_CONTENT: {
            const { contentId, contentName, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: { id: contentId, name: contentName },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SMS_CONTENT: {
            const { contentId, contentName, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: { id: contentId, name: contentName },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_TEMPLATE_ID: {
            const { templateId, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { templateId });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_SENDER_PROFILE_ID: {
            const { sendIndex, senderProfileId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { senderProfileId });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_SUBJECT_LINE: {
            const { sendIndex, subjectLine } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { subjectLine });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_PRE_HEADER: {
            const { preHeader, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { preHeader });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_SEND_NAME: {
            const { name, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { name });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SCHEDULED_TIMESTAMP: {
            const { scheduledTimestamp, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { scheduledTimestamp });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_HAS_RECOMMENDED_TIME: {
            const { hasRecommendedTime, sendIndex = 0 } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { hasRecommendedTime });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_RECURRENCE_FREQUENCY: {
            const { recurrenceFrequency } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { recurrenceFrequency });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SCHEDULED_END_TIMESTAMP: {
            const { scheduledEndTimestamp } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { scheduledEndTimestamp });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_PARENT_AUDIENCE_DELAY_INTERVAL: {
            const { parentAudienceDelayInterval, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { parentAudienceDelayInterval });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.ADD_CONDITIONAL_SPLIT_SENDS: {
            const { sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { splitType: SplitTypes.MEETS });

            const meetsCriteriaSend = state.sends[sendIndex];
            const doesNotMeetCriteriaSend = {
                ...meetsCriteriaSend,
                splitType: SplitTypes.DOES_NOT_MEET,
            };

            const sendsWithConditionalSplit = updatedSends.concat(doesNotMeetCriteriaSend);
            return { ...state, sends: sendsWithConditionalSplit };
        }
        case actionTypes.SET_SEGMENT: {
            const { segment: meetsCriteriaSegment, sendIndex } = action.parameters;

            const sendsWithSegments = getUpdatedSendsWithSegments(state.sends, meetsCriteriaSegment, sendIndex);

            return { ...state, sends: sendsWithSegments };
        }
        case actionTypes.SET_SPLIT_ACTION: {
            const { splitAction, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { splitAction });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.ADD_SEND: {
            const addedSend = {
                ...additionalEmailSend,
                parentIndex: action.parameters.parentIndex,
            };

            const updatedSends = [...state.sends, addedSend];
            return { ...state, sends: updatedSends };
        }
        case actionTypes.ADD_BRANCH_SEND: {
            const { parentIndex, splitType } = action.parameters;
            const addedSend = {
                ...additionalEmailSend,
                parentIndex,
                splitType,
                splitAction: SplitActions.SEND,
            };

            if (splitType === SplitTypes.MEETS) {
                const insertionIndex = state.sends.findIndex(
                    (send: any) => send.splitType === SplitTypes.DOES_NOT_MEET
                );

                if (insertionIndex !== -1) {
                    const updatedSends = [
                        ...state.sends.slice(0, insertionIndex),
                        addedSend,
                        ...state.sends.slice(insertionIndex),
                    ];

                    const adjustedSends = updatedSends.map((send: any, index: number) => {
                        if (index === insertionIndex) return send;
                        if (
                            send.parentIndex !== undefined &&
                            send.parentIndex !== null &&
                            send.parentIndex >= insertionIndex
                        ) {
                            return { ...send, parentIndex: send.parentIndex + 1 };
                        }
                        return send;
                    });

                    return { ...state, sends: adjustedSends };
                }
            }

            const updatedSends = [...state.sends, addedSend];
            return { ...state, sends: updatedSends };
        }
        case actionTypes.REMOVE_SEND: {
            const updatedSends = state.sends.slice(0, -1);
            return { ...state, sends: updatedSends };
        }
        case actionTypes.REMOVE_BRANCH_DESCENDANTS: {
            const { sendIndex } = action.parameters;

            const descendantIndices = new Set<number>();
            const collectDescendants = (parentIdx: number) => {
                state.sends.forEach((send: any, idx: number) => {
                    if (send.parentIndex === parentIdx && !descendantIndices.has(idx)) {
                        descendantIndices.add(idx);
                        collectDescendants(idx);
                    }
                });
            };
            collectDescendants(sendIndex);

            if (descendantIndices.size === 0) return state;

            const indexMapping = new Map<number, number>();
            let newIdx = 0;
            state.sends.forEach((_: any, oldIdx: number) => {
                if (!descendantIndices.has(oldIdx)) {
                    indexMapping.set(oldIdx, newIdx);
                    newIdx++;
                }
            });

            const filteredSends = state.sends
                .filter((_: any, idx: number) => !descendantIndices.has(idx))
                .map((send: any) => {
                    if (send.parentIndex != null) {
                        const mapped = indexMapping.get(send.parentIndex);
                        if (mapped !== undefined) return { ...send, parentIndex: mapped };
                    }
                    return send;
                });

            return { ...state, sends: filteredSends };
        }
        case actionTypes.RESET_CONDITIONAL_SPLIT_SEND: {
            const { sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                scheduledTimestamp: null,
                senderProfileId: null,
                subjectLine: '',
                templateId: null,
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.RESET_CONTENT: {
            const { sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: {
                    id: null,
                    name: '',
                },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_RE_ENTRY_DETAILS: {
            return {
                ...state,
                reentryDetails: action.parameters.reentryDetails,
            };
        }
        case actionTypes.CLEAR_RE_ENTRY_DETAILS: {
            return {
                ...state,
                reentryDetails: null,
            };
        }
        case actionTypes.SET_CALLER_PROFILE_ID: {
            const { sendIndex, callerProfileId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { callerProfileId });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SMS_SENDER_PROFILE_ID: {
            const { sendIndex, smsSenderProfileId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { smsSenderProfileId });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_AB_TEST_CONFIG: {
            const { abTestConfig } = action.parameters;

            return { ...state, abTestConfig };
        }
        case actionTypes.SET_AB_TEST_PERCENTAGE: {
            const { testPercentage } = action.parameters;

            return {
                ...state,
                abTestConfig: {
                    ...state.abTestConfig,
                    testPercentage,
                },
            };
        }
        case actionTypes.UPDATE_COMPARISON_FIELDS: {
            return updateABTestComparisonFields(state);
        }
        case actionTypes.REMOVE_AB_TEST_CONFIG: {
            const { abTestConfig, ...stateWithoutABTestConfig } = state;

            return stateWithoutABTestConfig;
        }
        case actionTypes.RESET_SENDS: {
            const currentChannel = state.sends[0]?.channel;
            const currentAudienceData = {
                segmentId: state.sends[0]?.segmentId,
                cohortId: state.sends[0]?.cohortId,
            };

            let initialSends;

            switch (currentChannel) {
                case CampaignChannels.EMAIL:
                    initialSends = emailInitialState.sends;
                    break;
                case CampaignChannels.SMS:
                    initialSends = smsInitialState.sends;
                    break;
                case CampaignChannels.DIRECT_MAIL:
                    initialSends = directMailInitialState.sends;
                    break;
                case CampaignChannels.VOICE_CALL:
                    initialSends = voiceCallInitialState.sends;
                    break;
                default:
                    initialSends = state.sends; // fallback to current state
            }

            // Reset to initial state but preserve audience selection
            const resetSends = [...initialSends];
            if (resetSends[0]) {
                resetSends[0] = {
                    ...resetSends[0],
                    ...currentAudienceData,
                };
            }

            return { ...state, sends: resetSends };
        }
        case actionTypes.SET_DRAFT_STATE: {
            // Action to set the entire state from draft data
            return action.draftState;
        }
    }

    throw Error('Unknown action: ' + action.type);
}
